/* eslint no-param-reassign: 0 */

import { clone, cloneDeep } from 'lodash';
import Vue from 'vue';

export const addSudoFeaturesToState = (state) => {
  const state2 = cloneDeep(state);

  state2.features.previousSudoers = { byId: {} };
  state2.features.sudoers = { byId: {} };

  return state2;
};

/**
 * After bound items are retrieved iterate through each one to set the sudoer
 * status of each bound item.
 */
export const processSudoBoundItems = (context, boundItems) => {
  boundItems.forEach((boundItem) => {
    const { compiledAttributes, id } = boundItem;

    if (compiledAttributes && compiledAttributes.sudo) {
      const item = {
        id,
        enabled: compiledAttributes.sudo.enabled,
        withoutPassword: compiledAttributes.sudo.withoutPassword,
      };
      context.commit('SET_PREVIOUS_SUDOER', item);
      context.commit('SET_SUDOER', item);
    }
  });
};

export const resetSudoState = (state, initialState) => {
  Vue.set(state.features, 'previousSudoers', initialState.features.previousSudoers);
  Vue.set(state.features, 'sudoers', initialState.features.sudoers);
};

/**
 * Generic method used to update sudoer status of an item in a byId hash. This
 * functionality is isolated because updating the sudo properties of an item in
 * either the previousSudoers or suders byId hash requires the same steps.
 */
export const updateSudoer = (byId, item) => {
  const { id, enabled, withoutPassword } = item;
  const noSudoAttrs = (!enabled && !withoutPassword);

  // Use Vue.set() in every item to make them reactive.
  // This ensures that any components that may have mapped getSudo() to a computed
  // property see the change and it's updated in their UI.
  // SEE:
  // https://stackoverflow.com/questions/38819289/why-is-computed-value-not-updated-after-vuex-store-update
  // https://vuejs.org/v2/api/#Vue-set
  Vue.set(byId, id, noSudoAttrs ? {} : { enabled, withoutPassword });
};

/**
 * The SudoMixin provides actions, getters, mutations and the initial state
 * used to handle the sudo status of users bound to a system and conversly for
 * systems bound to a user. It is currently used in the SystemUserCollection and
 * will be used in the SystemCollection.
 */
export default {
  actions: {
    setPreviousSudoer({ commit }, item) {
      commit('SET_PREVIOUS_SUDOER', item);
    },

    setPreviousSudoers({ commit }, items) {
      items.forEach((item) => {
        commit('SET_PREVIOUS_SUDOER', item);
      });
    },

    setSudoer({ commit }, item) {
      commit('SET_SUDOER', item);
    },

    setSudoers({ commit }, items) {
      items.forEach((item) => {
        commit('SET_SUDOER', item);
      });
    },
  },

  getters: {
    /**
     * Return a hash of user IDs to sudo attributes that need to be updated,
     * either because they did not previously exist, or their values have
     * changed.
     * @returns
     * {
     *   "user001": {
     *     "sudo": {
     *       eanbled: true,
     *       withoutPassword: false,
     *     },
     *   }
     *  }
     */
    getSudoersToUpdate(state, getters) {
      const previousSudoers = getters.getPreviousSudoers;
      const sudoers = getters.getSudoers;

      const sudoersToUpdate = {};

      Object.keys(sudoers).forEach((id) => {
        const sudoersEnabled = sudoers[id] ? sudoers[id].enabled : null;
        const sudoersWithoutPassword = sudoers[id] ? sudoers[id].withoutPassword : null;
        const previousSudoersEnabled = previousSudoers[id]
          ? previousSudoers[id].enabled : null;
        const previousSudoersWithoutPassword = previousSudoers[id]
          ? previousSudoers[id].withoutPassword : null;

        if (!previousSudoers[id]
            || sudoersEnabled !== previousSudoersEnabled
            || sudoersWithoutPassword !== previousSudoersWithoutPassword) {
          sudoersToUpdate[id] = !sudoersEnabled && !sudoersWithoutPassword ? {} : {
            sudo: clone(sudoers[id]),
          };
        }
      });

      return sudoersToUpdate;
    },

    /**
     * @todo Here we are allowing callers to get the list of previous sudoers so
     * that they can be used to _diff_ against the current set of sudoers. This
     * differs from how selected items are surfaced to callers from the
     * Collection Module. When handling selected items the previously selected
     * items are available in the store, but not used by callers to peform diffs.
     * Rather the getItemsToBind() and getItemsToUnbind() getters peform this
     * logic and simply return the list of items to bind and unbind, respectively.
     * For the sake of consitency, we should consider doing that here too!
     * @see bindAttributes() in /client/store/modules/DirectoryGroupingModule.js for details
     */
    getPreviousSudoers(state) {
      return state.features.previousSudoers.byId;
    },

    /**
     * @see https://vuex.vuejs.org/en/getters.html#method-style-access
     */
    getSudoer(state) {
      return id => state.features.sudoers.byId[id];
    },

    /**
     * Return sudoers who are currently selected so that sudo attributes aren't
     * accidentally updated on unselected items.
     */
    getSudoers(state, getters) {
      const selectedItems = getters.getSelectedItems;
      const byId = cloneDeep(state.features.sudoers.byId);

      Object.keys(byId).forEach((id) => {
        if (selectedItems.indexOf(id) === -1) {
          delete byId[id];
        }
      });

      return byId;
    },
  },

  mutations: {
    SET_PREVIOUS_SUDOER(state, item) {
      updateSudoer(state.features.previousSudoers.byId, item);
    },

    SET_SUDOER(state, item) {
      updateSudoer(state.features.sudoers.byId, item);
    },
  },
};
